import { CITY } from '@city'
import { useCallback, useMemo } from 'react'
import { StoreLinks } from '../../scripts/storeLinks.mjs'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import { reportStoreOutboundTapped, storeKindForUserAgent } from '../analytics/storeOutboundEvents'
import { storeCtaHref } from '../components/storeCtaHref'
import { useSchema } from '../components/useSchema'
import { resolveStationById } from '../interaction/stationResolver'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import { stationSnapshotFromSchema } from '../state/stationSnapshotFromSchema'
import { useMetroMapDispatch } from '../state/useMetroMapStore'
import type { LineView } from './buildLineView'

export interface LineViewActions {
  readonly onStationTap: (stationId: string) => void
  readonly onStatusExpand: () => void
  /**
   * Store link of the "get the app" CTA for this surface and visitor; `null`
   * in a city with no store apps (New York), where the CTA is not shown.
   */
  readonly ctaHref: string | null
  readonly onCtaTap: () => void
  readonly onClose: () => void
}

/**
 * What the line panel / card can do. `surface` picks the tracked store slot
 * — the desktop panel and the phone card are separate channels in the
 * clicks report (`web-line-panel` / `web-line-card`, Figma legend B·2 / C·2).
 */
export function useLineViewActions(
  view: Pick<LineView, 'lineId'> | null,
  surface: 'panel' | 'card',
  reporter: MetroAnalyticsReporter = realMetroAnalyticsReporter,
): LineViewActions {
  const schema = useSchema()
  const dispatch = useMetroMapDispatch()
  const slot = surface === 'panel' ? StoreLinks.SLOTS.LINE_PANEL : StoreLinks.SLOTS.LINE_CARD
  const store = storeKindForUserAgent(navigator.userAgent)
  // Hooks run before the host knows whether a line is open; with none the
  // actions are never reachable.
  const lineId = view?.lineId ?? ''

  const onStationTap = useCallback(
    (stationId: string) => {
      const resolved = resolveStationById(schema, stationId)
      if (resolved === null) return
      reporter('line_details_station_tapped', { line_id: lineId, station_id: stationId })
      dispatch({ kind: 'StationSelected', snapshot: stationSnapshotFromSchema(resolved) })
    },
    [schema, dispatch, reporter, lineId],
  )

  return useMemo(
    () => ({
      onStationTap,
      onStatusExpand: () => reporter('line_details_status_expanded', { line_id: lineId }),
      ctaHref: CITY.storeApps ? storeCtaHref(navigator.userAgent, store, slot) : null,
      onCtaTap: () => reportStoreOutboundTapped(reporter, { store, slot }),
      onClose: () => dispatch({ kind: 'LineCleared' }),
    }),
    [onStationTap, reporter, lineId, store, slot, dispatch],
  )
}
